// ============================================================
// HOOKED — LakeWorld3D.js        THE MOONLIT WHARF, IN THE ROUND
//
// THE RENDER LAYER ONLY PAINTS. Every shape in this file is
// DERIVED from the same MAP string array Lake.js has always used
// for collision — one land slab from the L bounding box, one pier
// from the D column, the shack from the ## footprint. Add a tile
// to the map and the world grows. Nothing here is authored per
// cell, and NOTHING here decides where you may stand.
//
// LAWS IN FORCE
//   • Palette law — SD is the WORLD. GM (chrome) appears only on
//     things that are literally made of brass and wood: the pier,
//     the shack, the sign. No menu colours in the water.
//   • mulberry32, seeded from the object's own index. NEVER
//     Math.random(), and never a PRNG call during render — every
//     seeded field is baked into a useMemo and read, not rolled.
//   • Every canvas texture is CACHED by key. Uncached is the
//     difference between working and not.
//   • colorSpace = SRGBColorSpace on every canvas texture, or the
//     palette comes out pale and you blame the palette.
//   • ONE useFrame per SYSTEM, never per object.
//   • ZERO new lights in the backdrop. Everything out past the
//     water is meshBasic / emissive / BackSide, and fog={false}.
//
// THE Y-ELEVATION TABLE (keep this current — two coplanar
// surfaces at the same Y is the flicker you will otherwise spend
// an hour hunting):
//     far water           -0.06   (flat)
//     moon glade          -0.02   (additive, on the far water)
//     near water           0.00   (displaced ±0.10)
//     foam / wet rim       0.320
//     LAND deck top        0.340
//     lantern pools        0.352
//     DOCK plank top       0.420
//     dock seams           0.424
// ============================================================

import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { SD, GM } from "./theme";

// ── World constants ──────────────────────────────────────────
export const UNIT     = 3.0;    // world units per map tile
export const WATER_Y  = 0.0;
export const LAND_Y   = 0.34;
export const DOCK_Y   = 0.42;
export const SEA_R    = 230;    // the water plane's reach
export const SKY_R    = 520;    // ⚠ measure every horizon element against THIS
const HORIZON_R       = 380;    // and keep them all measurably under it

// Population dials. If the backdrop ever costs a frame, these are
// the first and ONLY things to touch.
const N_TREES   = 74;
const N_SHARDS  = 9;
const N_MOTES   = 46;
const N_FLIES   = 28;
const N_REEDS   = 40;

// ── Deterministic variety ────────────────────────────────────
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);

// ── Texture cache ────────────────────────────────────────────
const TEX = new Map();
function tex(key, w, h, draw, repeat) {
  if (TEX.has(key)) return TEX.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  if (repeat) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat[0], repeat[1]);
  }
  t.needsUpdate = true;
  TEX.set(key, t);
  return t;
}

// ── Map helpers — the ONE place tile space becomes world space ─
export function makeWorld(MAP) {
  const COLS = MAP[0].length, ROWS = MAP.length;
  const toWorld = (tx, ty) => [
    (tx - COLS / 2 + 0.5) * UNIT,
    (ty - ROWS / 2 + 0.5) * UNIT,
  ];
  const toTile = (wx, wz) => [
    wx / UNIT + COLS / 2 - 0.5,
    wz / UNIT + ROWS / 2 - 0.5,
  ];
  return { COLS, ROWS, toWorld, toTile };
}

// Bounding box of every cell matching a predicate, in world units.
function bbox(MAP, pred) {
  const { COLS, ROWS, toWorld } = makeWorld(MAP);
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9, n = 0;
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    if (!pred(MAP[y][x], x, y)) continue;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x);
    y0 = Math.min(y0, y); y1 = Math.max(y1, y);
    n++;
  }
  if (!n) return null;
  const [ax, az] = toWorld(x0 - 0.5, y0 - 0.5);
  const [bx, bz] = toWorld(x1 + 0.5, y1 + 0.5);
  return { x0: ax, x1: bx, z0: az, z1: bz, w: bx - ax, d: bz - az,
           cx: (ax + bx) / 2, cz: (az + bz) / 2, n };
}

// ════════════════════════════════════════════════════════════
// THE WATER — the whole reason the screen is worth looking at
// ════════════════════════════════════════════════════════════
// Three sine rolls displace the geometry, and the normal is taken
// ANALYTICALLY from the derivative of that same sum. Exact
// lighting for the price of two extra multiplies, and no
// computeVertexNormals() in the frame loop.
// ⚠ SELF-CRITIQUE FIX (v1): the first pass used ONE 460-unit plane
// at 108 segments — 4.3 world units per segment. At UNIT=3 with a
// 1.8-unit man standing on it, the only waves that plane can
// RESOLVE have ~100-unit wavelengths, i.e. an ocean swell you
// cannot see. The water was mathematically animated and visually
// dead. So: TWO planes.
//   NEAR — 110u across at 128 segments (0.86u/segment) carries the
//          real chop, and it is the only one the player ever stands
//          near. The playable water is 48×18, so it is covered many
//          times over.
//   FAR  — 560u, flat, four triangles, same colour. It is the
//          horizon, and the fog eats it before anyone can tell.
const NEAR_R = 55;
const NEAR_SEG = 128;

// wavelength = 2π/k. These are 13u, 7u and 3.2u — a lake, not a sea.
const WAVES = [
  { dx:  0.87, dz:  0.49, k: 0.48, amp: 0.055, sp: 0.75 },
  { dx: -0.42, dz:  0.91, k: 0.90, amp: 0.030, sp: 1.15 },
  { dx:  0.64, dz: -0.77, k: 1.96, amp: 0.012, sp: 2.10 },
];

export function WaterSurface({ MAP, moonWorldX }) {
  const mesh = useRef();

  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(NEAR_R * 2, NEAR_R * 2, NEAR_SEG, NEAR_SEG);
    g.rotateX(-Math.PI / 2);
    return g;
  }, []);

  // ⚠ SELF-CRITIQUE FIX (v2): the near plane's troughs dip BELOW the
  // flat far plane, so the far water pokes up through the chop in a
  // ring you'd never guess the cause of. Taper the wave amplitude to
  // ZERO at the near plane's rim: the rim is then dead flat at y=0,
  // it covers the far plane cleanly, and there is no seam because
  // there is no step. Precomputed per vertex — never in the loop.
  const falloff = useMemo(() => {
    const pos = geo.attributes.position.array;
    const f = new Float32Array(pos.length / 3);
    const inner = NEAR_R * 0.70, outer = NEAR_R * 0.99;
    for (let i = 0, v = 0; i < pos.length; i += 3, v++) {
      const r = Math.hypot(pos[i], pos[i + 2]);
      const k = Math.min(1, Math.max(0, (outer - r) / (outer - inner)));
      f[v] = k * k * (3 - 2 * k);          // smoothstep
    }
    return f;
  }, [geo]);

  // Depth painting, derived from the MAP: the shallows read teal,
  // THE DEEP reads all but black, and it fades out to open water
  // past the map so there is no edge to find.
  const depthMap = useMemo(() => {
    const { COLS, ROWS } = makeWorld(MAP);
    return tex("water-depth", 256, 256, (g, w, h) => {
      g.fillStyle = SD.waterDeep; g.fillRect(0, 0, w, h);
      const mw = (COLS * UNIT) / (NEAR_R * 2) * w;
      const mh = (ROWS * UNIT) / (NEAR_R * 2) * h;
      const ox = (w - mw) / 2, oy = (h - mh) / 2;
      g.filter = "blur(7px)";
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
        const c = MAP[y][x];
        g.fillStyle = c === "w" ? SD.teal : c === "W" ? SD.waterDeep : SD.water;
        g.fillRect(ox + (x / COLS) * mw - 1, oy + (y / ROWS) * mh - 1,
                   mw / COLS + 2, mh / ROWS + 2);
      }
      g.filter = "none";
    });
  }, [MAP]);

  // THE MOON GLADE. It lives out on the FLAT far water, past the
  // near plane, which is both where a real one is and the only
  // place a flat additive plane cannot fight the chop for depth.
  const gladeMap = useMemo(() => tex("water-glade", 256, 256, (g, w, h) => {
    g.fillStyle = "#000"; g.fillRect(0, 0, w, h);
    const grd = g.createLinearGradient(0, 0, 0, h);
    grd.addColorStop(0, "rgba(232,240,221,0.95)");
    grd.addColorStop(0.42, "rgba(180,206,200,0.34)");
    grd.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = grd;
    g.beginPath();
    g.moveTo(w * 0.5 - w * 0.30, 0);
    g.lineTo(w * 0.5 + w * 0.30, 0);
    g.lineTo(w * 0.5 + w * 0.05, h);
    g.lineTo(w * 0.5 - w * 0.05, h);
    g.closePath(); g.fill();
    // broken by the chop — a glade is never a solid bar
    g.globalCompositeOperation = "destination-out";
    const R = mulberry32(31);
    for (let i = 0; i < 190; i++) {
      const yy = R() * h, ww = 10 + R() * 74, hh = 1.5 + R() * 3.5;
      g.fillStyle = `rgba(0,0,0,${0.25 + R() * 0.6})`;
      g.fillRect(w * 0.5 - ww / 2 + (R() - 0.5) * 62, yy, ww, hh);
    }
    g.globalCompositeOperation = "source-over";
  }), []);

  useFrame((state) => {
    const g = mesh.current?.geometry;
    if (!g) return;
    const t = state.clock.elapsedTime;
    const pos = g.attributes.position, nor = g.attributes.normal;
    const arr = pos.array, narr = nor.array;
    for (let i = 0, v = 0; i < arr.length; i += 3, v++) {
      const x = arr[i], z = arr[i + 2];
      const fall = falloff[v];
      if (fall === 0) { arr[i + 1] = 0; narr[i] = 0; narr[i + 1] = 1; narr[i + 2] = 0; continue; }
      let y = 0, gx = 0, gz = 0;
      for (let m = 0; m < WAVES.length; m++) {
        const W = WAVES[m];
        const ph = (x * W.dx + z * W.dz) * W.k + t * W.sp;
        y += Math.sin(ph) * W.amp;
        const c = Math.cos(ph) * W.amp * W.k;
        gx += c * W.dx;
        gz += c * W.dz;
      }
      y *= fall; gx *= fall; gz *= fall;
      arr[i + 1] = y;
      // the normal IS the derivative of the same sum — exact
      // lighting with no computeVertexNormals() in the loop
      const inv = 1 / Math.sqrt(gx * gx + 1 + gz * gz);
      narr[i] = -gx * inv; narr[i + 1] = inv; narr[i + 2] = -gz * inv;
    }
    pos.needsUpdate = true;
    nor.needsUpdate = true;
  });

  const GLADE_LEN = SEA_R - NEAR_R;
  return (
    <group>
      {/* FAR — flat, cheap, and fogged out before anyone reads it */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, WATER_Y - 0.06, 0]} raycast={() => null}>
        <planeGeometry args={[SEA_R * 2.4, SEA_R * 2.4]} />
        <meshStandardMaterial color={SD.waterDeep} roughness={0.2} metalness={0.6} />
      </mesh>

      {/* NEAR — the water you actually fish in */}
      <mesh ref={mesh} geometry={geo} position={[0, WATER_Y, 0]} receiveShadow>
        <meshStandardMaterial map={depthMap} color="#ffffff" roughness={0.15} metalness={0.55} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]}
            position={[moonWorldX * 0.55, WATER_Y - 0.02, -(NEAR_R + GLADE_LEN / 2)]}
            raycast={() => null}>
        <planeGeometry args={[96, GLADE_LEN]} />
        <meshBasicMaterial map={gladeMap} color={SD.moon} transparent opacity={0.6}
          blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} fog={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE LAND — one slab, derived from the L bounding box, run OFF
// the map on three sides so there is no floating rectangle.
// ════════════════════════════════════════════════════════════
function LandDeck({ MAP }) {
  const box = useMemo(() => bbox(MAP, (c) => c === "L" || c === "#"), [MAP]);
  const boards = useMemo(() => tex("shore-ground", 256, 256, (g, w, h) => {
    g.fillStyle = "#181008"; g.fillRect(0, 0, w, h);
    const R = mulberry32(77);
    // wet packed earth and gravel, not a tiled texture you can count
    for (let i = 0; i < 2600; i++) {
      const x = R() * w, y = R() * h, r = 0.6 + R() * 2.6;
      const v = R();
      g.fillStyle = v > 0.86 ? "#2c2114" : v > 0.55 ? "#1f1509" : "#120b05";
      g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    }
    for (let i = 0; i < 40; i++) {
      g.strokeStyle = `rgba(90,66,31,${0.05 + R() * 0.08})`;
      g.lineWidth = 0.6 + R() * 1.6;
      g.beginPath();
      g.moveTo(R() * w, R() * h);
      g.bezierCurveTo(R() * w, R() * h, R() * w, R() * h, R() * w, R() * h);
      g.stroke();
    }
  }, [7, 7]), []);

  if (!box) return null;
  const OVER = 26;   // how far the shore runs past the playable edge
  return (
    <group>
      <mesh position={[box.cx, LAND_Y - 0.5, box.cz + OVER / 2]} receiveShadow>
        <boxGeometry args={[box.w + OVER * 2, 1.0, box.d + OVER]} />
        <meshStandardMaterial map={boards} roughness={0.95} />
      </mesh>
      {/* THE WET RIM — where the lake actually meets the shore.
          Without this the land is a slab dropped in a pond. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[box.cx, 0.32, box.z0 - 0.02]}>
        <planeGeometry args={[box.w + OVER * 2, 2.4]} />
        <meshStandardMaterial color={SD.waterLight} roughness={0.25} metalness={0.4}
          transparent opacity={0.85} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[box.cx, 0.34, box.z0 - 0.6]} raycast={() => null}>
        <planeGeometry args={[box.w + OVER * 2, 1.1]} />
        <meshBasicMaterial color={SD.foam} transparent opacity={0.16} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE PIER — one structure, read from the D column.
// ════════════════════════════════════════════════════════════
function DockStructure3D({ MAP }) {
  const box = useMemo(() => bbox(MAP, (c) => c === "D"), [MAP]);
  const planks = useMemo(() => tex("dock-planks", 256, 256, (g, w, h) => {
    const R = mulberry32(19);
    g.fillStyle = GM.panel; g.fillRect(0, 0, w, h);
    const n = 7, bw = w / n;
    for (let i = 0; i < n; i++) {
      const v = R();
      g.fillStyle = v > 0.66 ? GM.panelHi : v > 0.33 ? GM.panel : GM.pageWarm;
      g.fillRect(i * bw + 1, 0, bw - 2, h);
      // grain
      for (let k = 0; k < 26; k++) {
        g.strokeStyle = `rgba(0,0,0,${0.05 + R() * 0.13})`;
        g.lineWidth = 0.5 + R();
        g.beginPath();
        const y0 = R() * h;
        g.moveTo(i * bw + 2, y0);
        g.bezierCurveTo(i * bw + bw * 0.4, y0 + (R() - 0.5) * 16,
                        i * bw + bw * 0.7, y0 + (R() - 0.5) * 16,
                        i * bw + bw - 2, y0 + (R() - 0.5) * 10);
        g.stroke();
      }
      // the dark seam between boards — this is what reads as planks
      g.fillStyle = "rgba(0,0,0,0.55)";
      g.fillRect(i * bw, 0, 1.6, h);
    }
  }, [3, 8]), []);

  const posts = useMemo(() => {
    if (!box) return [];
    const out = [];
    const R = rnd(404);
    const nz = Math.max(2, Math.round(box.d / (UNIT * 0.9)));
    for (let i = 0; i <= nz; i++) {
      const z = box.z0 + (box.d * i) / nz;
      for (const sx of [-1, 1]) {
        out.push({
          x: box.cx + sx * (box.w / 2 - 0.14),
          z,
          h: 1.5 + R() * 0.5,
          lean: (R() - 0.5) * 0.05,
          lamp: i % 2 === 0 && i < nz,
        });
      }
    }
    return out;
  }, [box]);

  if (!box) return null;
  return (
    <group>
      {/* the deck of the pier, run one tile past the last D so it
          ends OVER the water rather than at a hard edge */}
      <mesh position={[box.cx, DOCK_Y - 0.09, box.cz - UNIT * 0.35]} castShadow receiveShadow>
        <boxGeometry args={[box.w, 0.18, box.d + UNIT * 0.7]} />
        <meshStandardMaterial map={planks} roughness={0.88} />
      </mesh>
      {/* the pier's own shadow lying on the water */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[box.cx, 0.05, box.cz - UNIT * 0.35]} raycast={() => null}>
        <planeGeometry args={[box.w * 1.25, box.d + UNIT * 0.7]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.4} depthWrite={false} />
      </mesh>

      {posts.map((p, i) => (
        <group key={i} position={[p.x, 0, p.z]} rotation={[p.lean, 0, p.lean * 0.6]}>
          <mesh position={[0, p.h / 2 - 0.3, 0]} castShadow>
            <cylinderGeometry args={[0.11, 0.135, p.h + 0.6, 7]} />
            <meshStandardMaterial color={GM.ropeDim} roughness={0.92} />
          </mesh>
          {/* the wet line — every piling in the world has one */}
          <mesh position={[0, 0.12, 0]}>
            <cylinderGeometry args={[0.142, 0.142, 0.3, 7]} />
            <meshStandardMaterial color="#0b1a1e" roughness={0.55} metalness={0.2} />
          </mesh>
          {p.lamp && (
            <group position={[0, p.h + 0.02, 0]}>
              <mesh>
                <cylinderGeometry args={[0.07, 0.09, 0.2, 6]} />
                <meshStandardMaterial color={GM.gold} roughness={0.3} metalness={0.8} />
              </mesh>
              <mesh position={[0, 0.02, 0]}>
                <sphereGeometry args={[0.062, 10, 8]} />
                <meshBasicMaterial color={GM.goldPale} toneMapped={false} />
              </mesh>
              <mesh position={[0, 0.02, 0]}>
                <sphereGeometry args={[0.19, 10, 8]} />
                <meshBasicMaterial color={GM.gold} transparent opacity={0.15}
                  depthWrite={false} toneMapped={false} />
              </mesh>
              <pointLight intensity={2.4} distance={7.5} decay={1.5} color="#ffcf8a" castShadow={false} />
            </group>
          )}
        </group>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// WYLLY'S BAIT & TACKLE — a building that STANDS on the shore.
// Placement is read from the ## footprint in the MAP.
// ════════════════════════════════════════════════════════════
function Shack3D({ MAP }) {
  const box = useMemo(() => bbox(MAP, (c) => c === "#"), [MAP]);
  const smoke = useRef();
  const wall = useMemo(() => tex("shack-wall", 128, 128, (g, w, h) => {
    const R = mulberry32(53);
    g.fillStyle = GM.rope; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 9; i++) {
      g.fillStyle = R() > 0.5 ? GM.ropeLight : GM.ropeDim;
      g.globalAlpha = 0.25 + R() * 0.3;
      g.fillRect(0, (i * h) / 9, w, h / 9 - 1);
    }
    g.globalAlpha = 1;
    for (let i = 0; i <= 9; i++) {
      g.fillStyle = "rgba(0,0,0,0.42)";
      g.fillRect(0, (i * h) / 9 - 1, w, 1.6);
    }
  }, [2, 2]), []);

  useFrame((state) => {
    const s = smoke.current;
    if (!s) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < s.children.length; i++) {
      const m = s.children[i];
      const k = ((t * 0.34 + i / s.children.length) % 1);
      m.position.y = k * 3.4;
      m.position.x = Math.sin(k * 5 + i) * (0.15 + k * 0.7);
      m.scale.setScalar(0.16 + k * 0.85);
      m.material.opacity = 0.3 * (1 - k) * Math.min(1, k * 5);
    }
  });

  if (!box) return null;
  const W = Math.max(3.4, box.w * 0.92), D = Math.max(3.0, box.d * 0.86), H = 2.5;
  return (
    <group position={[box.cx, LAND_Y, box.cz]}>
      {/* walls */}
      <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[W, H, D]} />
        <meshStandardMaterial map={wall} roughness={0.9} />
      </mesh>
      {/* roof — two slabs, overhanging eaves */}
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * W * 0.26, H + 0.52, 0]} rotation={[0, 0, -s * 0.72]} castShadow>
          <boxGeometry args={[W * 0.78, 0.14, D + 0.8]} />
          <meshStandardMaterial color={GM.pageDeep} roughness={0.95} />
        </mesh>
      ))}
      <mesh position={[0, H + 1.03, 0]}>
        <boxGeometry args={[0.2, 0.16, D + 0.9]} />
        <meshStandardMaterial color={GM.panelEdge} roughness={0.9} />
      </mesh>

      {/* THE LIT WINDOW — the warmest thing on the lake */}
      <mesh position={[0, H * 0.56, D / 2 + 0.012]}>
        <planeGeometry args={[W * 0.3, H * 0.34]} />
        <meshBasicMaterial color={GM.goldPale} toneMapped={false} />
      </mesh>
      <mesh position={[0, H * 0.56, D / 2 + 0.02]}>
        <planeGeometry args={[W * 0.32, 0.035]} />
        <meshBasicMaterial color={GM.pageDeep} />
      </mesh>
      <mesh position={[0, H * 0.56, D / 2 + 0.02]}>
        <planeGeometry args={[0.035, H * 0.36]} />
        <meshBasicMaterial color={GM.pageDeep} />
      </mesh>
      {/* the door */}
      <mesh position={[W * 0.3, H * 0.42, D / 2 + 0.012]}>
        <planeGeometry args={[W * 0.2, H * 0.7]} />
        <meshStandardMaterial color={GM.pageDeep} roughness={0.9} />
      </mesh>

      {/* the light it SPILLS. Warm, decaying slowly, so it reaches
          the boards instead of lighting its own sill. */}
      <pointLight position={[0, H * 0.6, D / 2 + 1.1]} intensity={7.0} distance={15}
        decay={1.35} color="#ffbe6d" castShadow={false} />

      {/* chimney + smoke — one useFrame for the whole plume */}
      <mesh position={[-W * 0.3, H + 1.35, -D * 0.2]} castShadow>
        <boxGeometry args={[0.42, 1.5, 0.42]} />
        <meshStandardMaterial color="#2a1d10" roughness={0.95} />
      </mesh>
      <group ref={smoke} position={[-W * 0.3, H + 2.1, -D * 0.2]}>
        {Array.from({ length: 7 }).map((_, i) => (
          <mesh key={i} raycast={() => null}>
            <sphereGeometry args={[0.42, 8, 6]} />
            <meshBasicMaterial color="#8fa3a8" transparent opacity={0.2}
              depthWrite={false} fog={false} />
          </mesh>
        ))}
      </group>

      {/* THE HANGING SIGN — brass, and the ◆ Empire sigil struck in
          it. The world-side maker's mark (PRIME §1.15: the sigil
          lives HERE, never on the angler). */}
      <group position={[0, H * 0.92, D / 2 + 0.62]}>
        <mesh position={[0, 0.42, 0]}>
          <boxGeometry args={[1.7, 0.07, 0.07]} />
          <meshStandardMaterial color={GM.goldDeep} roughness={0.35} metalness={0.85} />
        </mesh>
        {[-0.62, 0.62].map((x) => (
          <mesh key={x} position={[x, 0.22, 0]}>
            <cylinderGeometry args={[0.014, 0.014, 0.4, 5]} />
            <meshStandardMaterial color={GM.ropeDim} roughness={0.9} />
          </mesh>
        ))}
        <mesh castShadow>
          <boxGeometry args={[1.5, 0.42, 0.06]} />
          <meshStandardMaterial color={GM.gold} roughness={0.3} metalness={0.9}
            emissive={GM.goldDeep} emissiveIntensity={0.3} />
        </mesh>
        <mesh position={[-0.52, 0, 0.05]} rotation={[0, 0, Math.PI / 4]}>
          <boxGeometry args={[0.17, 0.17, 0.03]} />
          <meshStandardMaterial color={GM.pageDeep} roughness={0.6} metalness={0.3} />
        </mesh>
      </group>

      {/* drying rack — a shack that WORKS */}
      <group position={[W * 0.42, 0, -D * 0.34]}>
        {[-0.7, 0.7].map((x) => (
          <mesh key={x} position={[x, 0.85, 0]} castShadow>
            <cylinderGeometry args={[0.05, 0.06, 1.7, 5]} />
            <meshStandardMaterial color={GM.ropeDim} roughness={0.92} />
          </mesh>
        ))}
        <mesh position={[0, 1.62, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.02, 0.02, 1.4, 5]} />
          <meshStandardMaterial color={GM.rope} roughness={0.9} />
        </mesh>
        {[-0.42, 0, 0.42].map((x, i) => (
          <mesh key={x} position={[x, 1.28, 0]} rotation={[0, 0, 0.06 * (i - 1)]}>
            <boxGeometry args={[0.22, 0.62, 0.04]} />
            <meshStandardMaterial color={i === 1 ? SD.tealDim : "#3a2a14"} roughness={0.85} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE FAR SHORE — depth-banded treeline. Painted, never modelled.
// ════════════════════════════════════════════════════════════
export function FarShore({ MAP }) {
  const water = useMemo(() => bbox(MAP, (c) => c === "W" || c === "w"), [MAP]);
  const trees = useMemo(() => {
    const R = rnd(913);
    return Array.from({ length: N_TREES }).map((_, i) => {
      const band = i % 3;                      // 0 near · 1 mid · 2 far
      const z = -60 - band * 34 - R() * 22;
      return {
        x: (R() - 0.5) * (260 + band * 120),
        z, band,
        h: (5.5 + R() * 7) * (1 + band * 0.42),
        r: 1.5 + R() * 1.3,
        lit: R() > 0.9 && band === 0,
      };
    });
  }, []);

  return (
    <group>
      {/* the far bank itself */}
      <mesh position={[0, 0.4, -118]} receiveShadow={false}>
        <boxGeometry args={[620, 1.2, 130]} />
        <meshStandardMaterial color="#050c14" roughness={1} />
      </mesh>
      {trees.map((t, i) => (
        <group key={i} position={[t.x, 0.9, t.z]}>
          <mesh raycast={() => null}>
            <coneGeometry args={[t.r, t.h, 6]} />
            <meshStandardMaterial
              color={t.band === 0 ? "#061420" : t.band === 1 ? "#08202e" : "#0c2a3c"}
              roughness={1}
            />
          </mesh>
          {t.lit && (
            <mesh position={[0, -t.h * 0.3, t.r * 0.8]} raycast={() => null}>
              <sphereGeometry args={[0.16, 8, 6]} />
              <meshBasicMaterial color={GM.goldPale} toneMapped={false} />
            </mesh>
          )}
        </group>
      ))}
      {/* MIST BANKS — three planes, no motion needed; the camera
          moving through them does the work */}
      {[-52, -84, -132].map((z, i) => (
        <mesh key={z} position={[0, 1.4 + i * 0.8, z]} raycast={() => null}>
          <planeGeometry args={[520, 9 + i * 5]} />
          <meshBasicMaterial color={SD.void4} transparent opacity={0.26 - i * 0.04}
            depthWrite={false} fog={false} />
        </mesh>
      ))}
      {water && <Reeds MAP={MAP} />}
    </group>
  );
}

function Reeds({ MAP }) {
  const grp = useRef();
  const reeds = useMemo(() => {
    const box = bbox(MAP, (c) => c === "w");
    const R = rnd(1201);
    if (!box) return [];
    return Array.from({ length: N_REEDS }).map(() => ({
      x: box.x0 + R() * box.w,
      z: box.z1 - R() * 3.2,
      h: 0.8 + R() * 1.1,
      p: R() * 7,
    }));
  }, [MAP]);

  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < g.children.length; i++) {
      g.children[i].rotation.z = Math.sin(t * 0.7 + reeds[i].p) * 0.14;
    }
  });

  return (
    <group ref={grp}>
      {reeds.map((r, i) => (
        <mesh key={i} position={[r.x, r.h / 2, r.z]} raycast={() => null}>
          <cylinderGeometry args={[0.012, 0.03, r.h, 4]} />
          <meshStandardMaterial color="#0d2a24" roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE NIGHT — sky dome, moon, stars.  Everything fog={false}.
// ════════════════════════════════════════════════════════════
export function NightSky({ moonWorldX }) {
  const skyTex = useMemo(() => tex("sky", 64, 512, (g, w, h) => {
    const grd = g.createLinearGradient(0, 0, 0, h);
    grd.addColorStop(0.00, SD.void0);
    grd.addColorStop(0.32, SD.void1);
    grd.addColorStop(0.58, SD.void2);
    grd.addColorStop(0.78, SD.void3);
    grd.addColorStop(0.92, SD.void4);
    grd.addColorStop(1.00, SD.void5);
    g.fillStyle = grd; g.fillRect(0, 0, w, h);
  }), []);

  const stars = useMemo(() => {
    const R = rnd(7);
    const pts = new Float32Array(520 * 3);
    for (let i = 0; i < 520; i++) {
      const th = R() * Math.PI * 2;
      const ph = Math.acos(1 - R() * 0.92);     // upper hemisphere, biased high
      const r = SKY_R * 0.86;
      pts[i * 3]     = r * Math.sin(ph) * Math.cos(th);
      pts[i * 3 + 1] = r * Math.cos(ph) + 20;
      pts[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pts, 3));
    return g;
  }, []);

  return (
    <group>
      <mesh scale={[-1, 1, 1]} raycast={() => null}>
        <sphereGeometry args={[SKY_R, 32, 20]} />
        <meshBasicMaterial map={skyTex} side={THREE.BackSide} fog={false} depthWrite={false} />
      </mesh>

      <points geometry={stars} raycast={() => null}>
        <pointsMaterial color={SD.starWhite} size={1.9} sizeAttenuation={false}
          transparent opacity={0.85} fog={false} depthWrite={false} />
      </points>

      {/* THE MOON — the light source the whole palette is named for */}
      <group position={[moonWorldX, 148, -HORIZON_R * 0.86]}>
        <mesh raycast={() => null}>
          <circleGeometry args={[19, 42]} />
          <meshBasicMaterial color={SD.moon} fog={false} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0, -1]} raycast={() => null}>
          <circleGeometry args={[44, 42]} />
          <meshBasicMaterial color={SD.moonHalo} transparent opacity={0.13}
            fog={false} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0, -2]} raycast={() => null}>
          <circleGeometry args={[96, 42]} />
          <meshBasicMaterial color={SD.moonHalo} transparent opacity={0.05}
            fog={false} toneMapped={false} depthWrite={false} />
        </mesh>
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE UNSTABLE WORLD — the standing horizon. Restrained here,
// because THE LAKE is a named place with its own weather: the
// eclipse sits LOW and far behind the treeline, and the shards
// are few. Three of the six elements, per the doctrine's floor.
//   ⚠ every element is measured against SKY_R (520) and sits
//   under HORIZON_R (380). Nothing hangs through the horizon.
// ════════════════════════════════════════════════════════════
export function UnstableHorizon() {
  const shards = useRef();
  const motes  = useRef();

  const eclipseTex = useMemo(() => tex("eclipse", 512, 512, (g, w, h) => {
    const cx = w / 2, cy = h / 2;
    g.clearRect(0, 0, w, h);
    const corona = g.createRadialGradient(cx, cy, w * 0.19, cx, cy, w * 0.5);
    corona.addColorStop(0, "rgba(214,69,69,0.55)");
    corona.addColorStop(0.35, "rgba(91,63,174,0.22)");
    corona.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = corona; g.beginPath(); g.arc(cx, cy, w * 0.5, 0, 7); g.fill();
    // the disc — a deep bruise, NEVER black
    g.fillStyle = "#140A22";
    g.beginPath(); g.arc(cx, cy, w * 0.2, 0, 7); g.fill();
    // the burning rim
    g.strokeStyle = "#ff8a5c"; g.lineWidth = 3.2;
    g.shadowColor = "#ff6b6b"; g.shadowBlur = 26;
    g.beginPath(); g.arc(cx, cy, w * 0.2, 0, 7); g.stroke();
    // THE CRACK — hotter than the rim, branching once at midpoint
    const R = mulberry32(4242);
    g.strokeStyle = "#ffd9a8"; g.shadowColor = "#ffb37a"; g.shadowBlur = 20;
    const draw = (x0, y0, x1, y1, n, wdt) => {
      g.lineWidth = wdt;
      g.beginPath(); g.moveTo(x0, y0);
      for (let i = 1; i <= n; i++) {
        const k = i / n;
        g.lineTo(x0 + (x1 - x0) * k + (R() - 0.5) * 16,
                 y0 + (y1 - y0) * k + (R() - 0.5) * 16);
      }
      g.stroke();
    };
    draw(cx - w * 0.19, cy - h * 0.07, cx + w * 0.18, cy + h * 0.1, 9, 2.6);
    draw(cx, cy + h * 0.015, cx + w * 0.13, cy - h * 0.15, 6, 1.6);
    g.shadowBlur = 0;
  }), []);

  const shardData = useMemo(() => {
    const R = rnd(88);
    return Array.from({ length: N_SHARDS }).map(() => ({
      x: (R() - 0.5) * HORIZON_R * 1.5,
      y: 42 + R() * 120,
      z: -HORIZON_R * (0.5 + R() * 0.42),
      s: 6 + R() * 16,
      sq: [0.6 + R() * 0.9, 0.5 + R() * 1.1, 0.7 + R() * 0.8],
      sp: 0.03 + R() * 0.06,
      bob: R() * 7,
      core: R() > 0.5 ? SD.purpleLight : SD.tealLight,
    }));
  }, []);

  const moteData = useMemo(() => {
    const R = rnd(1717);
    return Array.from({ length: N_MOTES }).map(() => ({
      x: (R() - 0.5) * 200, z: -20 - R() * 200,
      y0: R() * 60, sp: 1.4 + R() * 3.4, s: 0.2 + R() * 0.5,
    }));
  }, []);

  // ⚑ ONE useFrame per SYSTEM. Two here, not thirty-five.
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const g = shards.current;
    if (g) for (let i = 0; i < g.children.length; i++) {
      const d = shardData[i], m = g.children[i];
      m.rotation.y = t * d.sp;
      m.rotation.x = Math.sin(t * d.sp * 0.7 + d.bob) * 0.25;
      m.position.y = d.y + Math.sin(t * 0.22 + d.bob) * 3.2;
    }
    const p = motes.current;
    if (p) {
      const arr = p.geometry.attributes.position.array;
      for (let i = 0; i < moteData.length; i++) {
        const d = moteData[i];
        arr[i * 3 + 1] = ((d.y0 + t * d.sp) % 90) - 4;   // everything RISES
      }
      p.geometry.attributes.position.needsUpdate = true;
    }
  });

  const motesGeo = useMemo(() => {
    const arr = new Float32Array(N_MOTES * 3);
    moteData.forEach((d, i) => { arr[i * 3] = d.x; arr[i * 3 + 1] = d.y0; arr[i * 3 + 2] = d.z; });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    return g;
  }, [moteData]);

  return (
    <group>
      {/* THE CRACKED ECLIPSE — low, far, behind the treeline, so it
          belongs to the sky over the lake rather than owning it */}
      <mesh position={[-HORIZON_R * 0.52, 74, -HORIZON_R * 0.9]} raycast={() => null}>
        <planeGeometry args={[132, 132]} />
        <meshBasicMaterial map={eclipseTex} transparent depthWrite={false}
          fog={false} toneMapped={false} opacity={0.72} />
      </mesh>

      {/* SHATTERED POCKET DIMENSIONS — each lit from INSIDE */}
      <group ref={shards}>
        {shardData.map((d, i) => (
          <group key={i} position={[d.x, d.y, d.z]} scale={d.sq} raycast={() => null}>
            <mesh>
              <octahedronGeometry args={[d.s, 0]} />
              <meshBasicMaterial color="#0a0a16" fog={false} />
            </mesh>
            <mesh scale={0.74}>
              <octahedronGeometry args={[d.s, 0]} />
              <meshBasicMaterial color={d.core} fog={false} toneMapped={false} />
            </mesh>
            <mesh scale={1.5}>
              <octahedronGeometry args={[d.s, 0]} />
              <meshBasicMaterial color={d.core} transparent opacity={0.08}
                side={THREE.BackSide} depthWrite={false} fog={false} />
            </mesh>
          </group>
        ))}
      </group>

      {/* THE RISE */}
      <points ref={motes} geometry={motesGeo} raycast={() => null}>
        <pointsMaterial color={SD.caustic} size={2.4} sizeAttenuation={false}
          transparent opacity={0.4} depthWrite={false} fog={false} />
      </points>
    </group>
  );
}

// ── FIREFLIES — the near-field version of the same idea ──────
export function Fireflies({ MAP }) {
  const grp = useRef();
  const data = useMemo(() => {
    const box = bbox(MAP, (c) => c === "L" || c === "#");
    const R = rnd(555);
    if (!box) return [];
    return Array.from({ length: N_FLIES }).map(() => ({
      x: box.x0 + R() * box.w, z: box.z0 + R() * box.d,
      y: 0.7 + R() * 2.4, p: R() * 7, r: 0.6 + R() * 1.6, sp: 0.25 + R() * 0.5,
    }));
  }, [MAP]);

  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < g.children.length; i++) {
      const d = data[i], m = g.children[i];
      m.position.set(
        d.x + Math.sin(t * d.sp + d.p) * d.r,
        d.y + Math.sin(t * d.sp * 1.7 + d.p) * 0.35,
        d.z + Math.cos(t * d.sp * 0.8 + d.p * 1.3) * d.r
      );
      m.material.opacity = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.1 + d.p * 3));
    }
  });

  return (
    <group ref={grp}>
      {data.map((d, i) => (
        <mesh key={i} raycast={() => null}>
          <sphereGeometry args={[0.045, 6, 5]} />
          <meshBasicMaterial color={GM.goldPale} transparent opacity={0.5}
            depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE WORLD — one export. The screen mounts this and nothing else.
// (Its water, night, horizon, far shore and fireflies are ALSO exported,
// unchanged, for Wharf3D — the lake off the end of the Pier, 2026-09-25.)
// ════════════════════════════════════════════════════════════
export function LakeWorld3D({ MAP }) {
  const moonWorldX = -HORIZON_R * 0.18;
  return (
    <group>
      <NightSky moonWorldX={moonWorldX} />
      <UnstableHorizon />
      <FarShore MAP={MAP} />
      <WaterSurface MAP={MAP} moonWorldX={moonWorldX} />
      <LandDeck MAP={MAP} />
      <DockStructure3D MAP={MAP} />
      <Shack3D MAP={MAP} />
      <Fireflies MAP={MAP} />
    </group>
  );
}
